import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Globe, Bot, ArrowRight, 
  ChevronRight, ChevronLeft, Code, Zap
} from 'lucide-react';

// --- 1. GÖRSEL VE VERİ VARLIKLARI ---
const images = {
  logoWhite: "/exereyes_logo_whitebr.png",
  slider1: "banner_2.png",
  slider2: "sandalye_triceps.png",
  slider3: "banner.png",
  slider4: "exereyes_promo_1.png", // Üç panelli EXEREYES promosyon görseli
  slider5: "exereyes_promo_2.png", // AI BASED TRAINER telefon görseli
  appInterface1: "https://images.unsplash.com/photo-1551650975-87deedd944c3?q=80&w=1974&auto=format&fit=crop",
  appInterface3: "https://images.unsplash.com/photo-1576678927484-cc907957088c?q=80&w=987&auto=format&fit=crop",
  appInterface4: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=987&auto=format&fit=crop",
  exerciseWaterBottle: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=1470&auto=format&fit=crop", 
  exerciseBackpack: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=80&w=1470&auto=format&fit=crop",
  exerciseChair: "https://images.unsplash.com/photo-1598289431512-b97b0917affc?q=80&w=1474&auto=format&fit=crop",
};

// --- 2. TEMEL BİLEŞENLER ---

// BUTTON
interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'text' | 'white';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  to?: string;
}

const Button: React.FC<ButtonProps> = ({
  children, variant = 'primary', size = 'md', className = '', onClick, fullWidth = false, icon, iconPosition = 'left', to
}) => {
  const baseStyles = `inline-flex items-center justify-center font-medium tracking-wide rounded-full transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 active:scale-95`;
  
  const variantStyles = {
    primary: `bg-slate-900 text-white hover:bg-slate-800 shadow-lg shadow-slate-900/20 hover:-translate-y-0.5 focus:ring-slate-500`,
    secondary: `bg-amber-500 text-white hover:bg-amber-400 shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:-translate-y-0.5 focus:ring-amber-500`,
    outline: `border-2 border-slate-900 text-slate-900 bg-transparent hover:bg-slate-50 focus:ring-slate-500`,
    text: `text-slate-200 hover:text-white hover:bg-white/10 focus:ring-slate-500`,
    white: `bg-white text-slate-900 hover:bg-gray-100 shadow-lg hover:shadow-xl hover:-translate-y-0.5 focus:ring-white/50`
  };
  
  const sizeStyles = {
    sm: "text-xs px-4 py-2",
    md: "text-sm px-6 py-3",
    lg: "text-base px-8 py-4",
  };

  const content = (
    <>
      {icon && iconPosition === 'left' && <span className={`${children ? 'mr-2' : ''}`}>{icon}</span>}
      {children}
      {icon && iconPosition === 'right' && <span className={`${children ? 'ml-2' : ''}`}>{icon}</span>}
    </>
  );

  if (to) {
    return <Link to={to} className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${fullWidth ? "w-full" : ""} ${className}`}>{content}</Link>;
  }
  
  return (
    <button className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${fullWidth ? "w-full" : ""} ${className}`} onClick={onClick}>
      {content}
    </button>
  );
};

// SECTION (GÜNCELLENDİ: 'gold' varyantı #fdfdfd yapıldı)
const Section: React.FC<{ 
  children: React.ReactNode; 
  className?: string; 
  title?: string; 
  subtitle?: string; 
  centered?: boolean; 
  id?: string; 
  variant?: 'dark' | 'light' | 'gradient' | 'gold'; 
  withPattern?: boolean;
  patternVariant?: 'dots' | 'grid';
}> = ({ 
  children, className = '', title, subtitle, centered = false, id, variant = 'dark', withPattern = false, patternVariant = 'dots'
}) => {
  
  const styles = {
    dark: 'bg-slate-900 text-white',
    light: 'bg-slate-50 text-slate-900',
    gradient: 'bg-gradient-to-br from-slate-900 to-slate-800 text-white',
    gold: 'bg-[#fdfdfd] text-slate-900', // GÜNCELLENDİ: İstenilen özel arka plan rengi
  };
  
  return (
    <section id={id} className={`relative py-24 md:py-32 overflow-hidden ${styles[variant]} ${className}`}>
      
      {withPattern && (
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
          {patternVariant === 'dots' ? (
            <div style={{ backgroundImage: `radial-gradient(currentColor 1px, transparent 1px)`, backgroundSize: '32px 32px' }} className="h-full w-full"></div>
          ) : (
            <div 
              className="h-full w-full"
              style={{ 
                backgroundImage: 'linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)', 
                backgroundSize: '40px 40px' 
              }}
            ></div>
          )}
        </div>
      )}

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {(title || subtitle) && (
          <div className={`mb-16 ${centered ? 'text-center mx-auto max-w-3xl' : 'max-w-3xl'}`}>
            {title && (
              <motion.h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                {title}
              </motion.h2>
            )}
            {subtitle && (
              <motion.p className={`text-lg md:text-xl leading-relaxed opacity-80`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
                {subtitle}
              </motion.p>
            )}
            <div className={`h-1 w-20 bg-amber-500 rounded-full mt-6 ${centered ? 'mx-auto' : ''}`}></div>
          </div>
        )}
        {children}
      </div>
    </section>
  );
};

// --- 3. LOTUS SPORT HOME PAGE (YENİDEN TASARLANDI - DİNAMİK SLIDER) ---

const LotusHome: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true); // Hover durumunda durdurmak için

  const slides = [
    {
      id: 1,
      title: "Sporda Geleceği Şekillendiren Teknoloji",
      description: "Lotus Sport olarak, spor endüstrisine yenilikçi çözümler ve ileri teknolojiler sunarak sporu toplum tabanına yayıyoruz.",
      buttonText: "Hakkımızda",
      buttonLink: "/about",
      image: images.slider1
    },
    {
      id: 2,
      title: "Exereyes: Sıfır Ekipman ile Her Yerde Spor",
      description: "%100 yerli mühendislik ile geliştirilen özgün yapay zeka teknolojimiz. Evinizdeki sıradan nesneleri (sandalye, şişe, çanta) egzersiz ekipmanına dönüştüren, Google Play Store'da yayınlanmış çalışan teknoloji.",
      buttonText: "Exereyes'ı Keşfet",
      buttonLink: "/exereyes",
      image: images.slider2
    },
    {
      id: 3,
      title: "Yenilikçi Spor Teknolojileri",
      description: "Yapay zeka destekli egzersiz programlarımızla, fiziksel sağlığınızı üst seviyeye çıkarmayı hedefliyoruz.",
      buttonText: "Teknolojilerimiz",
      buttonLink: "/projects",
      image: images.slider3
    },
    {
      id: 4,
      title: "Evde Spor Yapmanın Yeni Yolu",
      description: "Evinizdeki sıradan nesneleri egzersiz ekipmanına dönüştüren devrimsel teknoloji. Eviniz spor salonunuz, eşyalarınız ekipmanınız.",
      buttonText: "Exereyes'ı Keşfet",
      buttonLink: "/exereyes",
      image: images.slider4
    },
    {
      id: 5,
      title: "Yapay Zeka Destekli Kişisel Antrenörünüz",
      description: "Geleceğin antrenörü artık cebinizde. AI tabanlı kişiselleştirilmiş antrenman deneyimi ile hedeflerinize ulaşın.",
      buttonText: "Hemen İndir",
      buttonLink: "/exereyes",
      image: images.slider5
    }
  ];

  // Slider Timer logic
  useEffect(() => {
    if (!isAutoPlay) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    
    return () => clearInterval(timer);
  }, [isAutoPlay, slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setIsAutoPlay(false); // Manuel müdahalede otomatik oynatmayı durdurabiliriz veya resetleyebiliriz
    setTimeout(() => setIsAutoPlay(true), 10000); // 10 sn sonra tekrar başlat
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setIsAutoPlay(false);
    setTimeout(() => setIsAutoPlay(true), 10000);
  };

  return (
    <>
      {/* CINEMATIC HERO SLIDER */}
      <section className="relative h-screen min-h-[600px] overflow-hidden bg-slate-900 group">
        
        {/* Arka Plan Geçişleri */}
        <AnimatePresence mode='wait'>
          <motion.div
            key={slides[currentSlide].id}
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.1 }} // Ken Burns Effect: Zoom Out
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          >
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
              style={{ backgroundImage: `url(${slides[currentSlide].image})` }} 
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
          </motion.div>
        </AnimatePresence>

        {/* İçerik */}
        <div className="container relative z-10 h-full mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-3xl pt-20">
            <AnimatePresence mode='wait'>
              <motion.div
                key={`text-${currentSlide}`}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={{
                  hidden: { opacity: 0, x: -50 },
                  visible: { opacity: 1, x: 0, transition: { staggerChildren: 0.15, delayChildren: 0.2 } },
                  exit: { opacity: 0, x: 50, transition: { duration: 0.3 } }
                }}
              >
                <motion.div variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 text-sm font-medium mb-6 backdrop-blur-sm">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span> Lotus Sport Technology
                  </div>
                </motion.div>

                <motion.h1 variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }} className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6 drop-shadow-lg">
                  {slides[currentSlide].title}
                </motion.h1>

                <motion.p variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }} className="text-xl text-slate-200 mb-10 leading-relaxed max-w-2xl drop-shadow-md">
                  {slides[currentSlide].description}
                </motion.p>

                <motion.div variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }} className="flex gap-4">
                  <Button to={slides[currentSlide].buttonLink} variant="secondary" size="lg" icon={<ArrowRight size={20}/>} iconPosition="right">
                    {slides[currentSlide].buttonText}
                  </Button>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Navigasyon Okları (Hover'da görünür) */}
        <div className="absolute top-1/2 right-4 md:right-10 -translate-y-1/2 flex flex-col gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
           <button onClick={prevSlide} className="p-3 rounded-full bg-white/10 hover:bg-amber-500 hover:text-white text-white backdrop-blur-sm border border-white/10 transition-all"><ChevronLeft size={24} /></button>
           <button onClick={nextSlide} className="p-3 rounded-full bg-white/10 hover:bg-amber-500 hover:text-white text-white backdrop-blur-sm border border-white/10 transition-all"><ChevronRight size={24} /></button>
        </div>

        {/* Slider Indicators & Progress */}
        <div className="absolute bottom-10 left-0 w-full z-20">
          <div className="container mx-auto px-4 flex items-center gap-4">
            {/* İlerleme Çubuğu */}
            <div className="flex gap-2">
              {slides.map((_, idx) => (
                <div key={idx} className="relative h-1.5 w-16 bg-white/20 rounded-full overflow-hidden cursor-pointer" onClick={() => setCurrentSlide(idx)}>
                  {currentSlide === idx && (
                    <motion.div 
                      className="absolute inset-0 bg-amber-500"
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 6, ease: "linear" }} // Timer ile senkronize
                    />
                  )}
                  {currentSlide > idx && <div className="absolute inset-0 bg-amber-500/50 w-full" />}
                </div>
              ))}
            </div>
            {/* Sayısal Gösterge */}
            <span className="text-white/80 font-mono text-sm">
               0{currentSlide + 1} / 0{slides.length}
            </span>
          </div>
        </div>
      </section>

      {/* HİZMETLER SECTION (YENİLENMİŞ TASARIM) */}
      <Section variant="light" title="Hizmetlerimiz" subtitle="Teknoloji ve sporu birleştirerek sınırları zorluyoruz." centered>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <ServiceCard 
            title="Görüntü İşleme Teknolojisi" 
            description="%100 yerli mühendislik ile geliştirilen özgün yapay zeka teknolojimiz. Telefon kamerasıyla ortamı tarayarak evdeki nesneleri egzersiz ekipmanına dönüştürme."
            icon={<Bot className="w-12 h-12" />} 
          />
          <ServiceCard 
            title="Kişiselleştirilmiş Antrenman" 
            description="Kullanıcının kişisel verilerine göre özelleştirilen antrenman programları. Üç boyutlu robot eğitmen ve yapay zeka destekli analizlerle gelişim takibi."
            icon={<Code className="w-12 h-12" />} 
          />
          <ServiceCard 
            title="Herkes İçin Spor" 
            description="Gençlik ve Spor Bakanlığı'nın 'Herkes İçin Spor' hedeflerini destekleyerek, kadınlar, yaşlılar, gençler ve kırsal bölgelerde yaşayan bireyler için spora erişim engelini ortadan kaldırıyoruz."
            icon={<Globe className="w-12 h-12" />} 
          />
        </div>
      </Section>

      {/* MARKALAR SECTION (DÜZENLENDİ: Custom Background #fdfdfd & Card Colors) */}
      <Section variant="gold" withPattern patternVariant="grid" title="Markalarımız" subtitle="Geleceği inşa eden projelerimiz." centered>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
          
          {/* Exereyes Card */}
          {/* GÜNCELLENDİ: Varsayılan Beyaz, Hover rengi #4C1FA4 */}
          <motion.div 
            whileHover={{ y: -10 }}
            className="group relative bg-white hover:bg-[#4C1FA4] rounded-[2.5rem] p-10 border border-slate-200 hover:border-slate-700 overflow-hidden shadow-2xl transition-colors duration-500"
          >
            {/* Hover Background Glow */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/3 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div className="relative z-10">
              {/* Logo Container: Arka Plan #DA862E (Turuncu) */}
              <div className="w-24 h-24 bg-[#DA862E] rounded-2xl flex items-center justify-center mb-8 shadow-lg transition-transform duration-500 group-hover:scale-105">
                 <img src={images.logoWhite} alt="Logo" className="w-14 h-14 object-contain" />
              </div>
              
              {/* Texts */}
              <h3 className="text-3xl font-bold text-slate-900 group-hover:text-white mb-4 transition-colors duration-500">Exereyes</h3>
              <p className="text-slate-600 group-hover:text-slate-300 text-lg mb-8 leading-relaxed transition-colors duration-500">
                %100 yerli mühendislik ile geliştirilen özgün yapay zeka teknolojisi. Görüntü işleme teknolojisi kullanarak evdeki sıradan eşyaları (sandalye, şişe, çanta vb.) egzersiz ekipmanına dönüştüren, Google Play Store'da yayınlanmış mobil uygulama.
              </p>
              
              {/* Link */}
              <Link to="/exereyes" className="inline-flex items-center text-[#DA862E] group-hover:text-[#DA862E] font-bold transition-colors text-lg bg-transparent group-hover:bg-white/10 px-4 py-2 rounded-full -ml-4 group-hover:ml-0">
                Keşfet <ArrowRight className="ml-2 w-6 h-6" />
              </Link>
            </div>
          </motion.div>

          {/* Coming Soon Card */}
          <div className="relative bg-white rounded-[2.5rem] p-10 border border-slate-200 overflow-hidden flex flex-col justify-center shadow-xl shadow-slate-200/50">
             <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03]"></div>
             <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mb-6 text-slate-400">
                <Zap size={40} />
             </div>
             <h3 className="text-2xl font-bold text-slate-800 mb-3">Yeni Projeler</h3>
             <p className="text-slate-500 text-lg">Ar-Ge ekibimiz sporda devrim yaratacak yeni teknolojiler üzerinde çalışıyor.</p>
             <span className="absolute top-8 right-8 px-4 py-1.5 bg-slate-100 rounded-full text-xs font-bold text-slate-500 uppercase tracking-wider">Yakında</span>
          </div>
        </div>
      </Section>
    </>
  );
};

// --- ÖZELLEŞTİRİLMİŞ SERVICE CARD ---
const ServiceCard: React.FC<{ title: string; description: string; icon: React.ReactNode }> = ({ title, description, icon }) => (
  <motion.div 
    whileHover={{ y: -10 }}
    className="group bg-white p-10 rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100 hover:border-amber-400/40 transition-all duration-500 relative overflow-hidden"
  >
    {/* Dekoratif Arka Plan Şekli */}
    <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-bl-[100px] -mr-8 -mt-8 transition-all group-hover:bg-amber-500/10"></div>
    
    <div className="relative z-10">
      <div className="w-24 h-24 bg-amber-50 rounded-3xl flex items-center justify-center text-amber-600 mb-8 group-hover:scale-110 group-hover:bg-amber-500 group-hover:text-white transition-all duration-500 shadow-lg shadow-amber-500/10">
        {icon}
      </div>
      <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-amber-600 transition-colors duration-300">{title}</h3>
      <p className="text-lg text-slate-600 leading-relaxed">{description}</p>
    </div>
  </motion.div>
);

const Home: React.FC = () => <LotusHome />;

export default Home;